import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import { parsePrefixes } from '@nu/shared';
import { api } from '../../api/client.js';
import Modal from '../../components/Modal.jsx';
import { problem } from '../bookings/util.js';

// Operations > Teams: every team, its members today, its Network Plus gang
// number (five digits, goes in the survey sheet's Gang Name column) and the
// postcodes it covers (survey jobs go to it by postcode). Add, rename, set
// the gang, close or reopen; a team that was never used can be deleted, one
// with history can only be closed. Who's in which team is still Match-Ups.
export default function Teams({ me }) {
  const canEdit = me.capabilities.includes('page.matchups.edit');
  const [teams, setTeams] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // a team, or { new: true }
  const [notice, setNotice] = useState(null);
  const [showClosed, setShowClosed] = useState(false);

  const load = useCallback(() => api('/teams').then((d) => { setTeams(d.teams); setError(null); }, (e) => setError(problem(e))), []);
  useEffect(() => { load(); }, [load]);

  const shown = (teams || []).filter((t) => showClosed || t.active);
  const closed = (teams || []).filter((t) => !t.active).length;

  return (
    <div className="stack">
      <section className="card">
        <div className="page-head">
          <div>
            <h1>Teams</h1>
            <p className="muted">Every team with its members today, its Network Plus gang number and the postcodes it covers. Survey jobs go to the team covering their postcode. Who is in which team is set on <Link to="/match-ups">Match-Ups</Link>.</p>
          </div>
          {canEdit && <button type="button" className="btn primary with-icon" onClick={() => setEditing({ new: true, name: '', gangName: '', prefixes: [] })}><Plus size={18} aria-hidden="true" /> New team</button>}
        </div>
        {notice && <p className={`notice-line notice-${notice.tone}`} role="status">{notice.text}</p>}
        {error && <p className="notice-line notice-bad">{error}</p>}
        {!teams && !error && <p className="muted">Loading…</p>}
      </section>

      {teams && (
        <section className="card">
          {closed > 0 && (
            <label className="check small teams-closed">
              <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} /> Show closed teams ({closed})
            </label>
          )}
          <div className="table-scroll">
            <table className={`table${canEdit ? ' clickable' : ''}`}>
              <thead><tr><th>Team</th><th>Members</th><th>Gang number</th><th>Postcodes</th><th className="num">Open surveys</th><th>Status</th></tr></thead>
              <tbody>
                {shown.map((t) => (
                  <tr key={t.id} className={t.active ? '' : 'inactive'} tabIndex={canEdit ? 0 : undefined}
                    onClick={canEdit ? () => setEditing(t) : undefined}
                    onKeyDown={canEdit ? (e) => { if (e.key === 'Enter') setEditing(t); } : undefined}>
                    <td><strong>{t.name}</strong>{t.supervisorName && <div className="small muted">Supervisor: {t.supervisorName}</div>}</td>
                    <td className="small">{t.members || <span className="muted">Nobody</span>}</td>
                    <td>{t.gangName ? <strong>{t.gangName}</strong> : <span className="muted small">Not set</span>}</td>
                    <td className="small">{t.prefixes.join(', ') || <span className="muted">None</span>}</td>
                    <td className="num">{t.openSurveys}</td>
                    <td><span className={`status ${t.active ? 'status-completed' : 'status-not_started'}`}>{t.active ? 'Active' : 'Closed'}</span></td>
                  </tr>
                ))}
                {!shown.length && <tr><td colSpan={6} className="muted">No teams yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {editing && (
        <TeamEditor
          team={editing}
          onClose={() => setEditing(null)}
          onDone={(text) => { setEditing(null); setNotice({ tone: 'ok', text }); load(); }}
        />
      )}
    </div>
  );
}

function TeamEditor({ team, onClose, onDone }) {
  const isNew = Boolean(team.new);
  const [name, setName] = useState(team.name || '');
  const [gang, setGang] = useState(team.gangName || '');
  const [prefixes, setPrefixes] = useState((team.prefixes || []).join(', '));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const tidy = parsePrefixes(prefixes);
  const gangOk = gang === '' || /^\d{5}$/.test(gang);

  const call = async (fn, done) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onDone(done);
    } catch (e) {
      setError(problem(e));
      setBusy(false);
    }
  };
  const save = () => call(
    () => (isNew
      ? api('/teams', { method: 'POST', body: { name, gangName: gang, prefixes } })
      : api(`/teams/${team.id}`, { method: 'PUT', body: { name, gangName: gang, prefixes } })),
    isNew ? `${name} added.` : `${name} saved.`,
  );
  const setActive = (active) => call(() => api(`/teams/${team.id}`, { method: 'PUT', body: { active } }), `${team.name} ${active ? 'reopened' : 'closed'}.`);
  const remove = () => call(() => api(`/teams/${team.id}`, { method: 'DELETE' }), `${team.name} deleted.`);

  return (
    <Modal
      open
      title={isNew ? 'New team' : `Team: ${team.name}`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="button" className="btn primary" disabled={busy || !name.trim() || !gangOk} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
        </>
      )}
    >
      <div className="stack">
        <label className="fld">
          <span>Name</span>
          <input value={name} maxLength={60} autoFocus onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="fld narrow">
          <span>Gang number <span className="small muted">(Network Plus, 5 digits)</span></span>
          <input value={gang} inputMode="numeric" maxLength={5} onChange={(e) => setGang(e.target.value.replace(/\D/g, ''))} placeholder="e.g. 18647" />
          {!gangOk && <span className="small warn-text">Five digits.</span>}
        </label>
        <label className="fld">
          <span>Postcodes covered <span className="small muted">(areas LE, districts LE13 or sectors LE13 1, with commas)</span></span>
          <input value={prefixes} onChange={(e) => setPrefixes(e.target.value)} placeholder="e.g. LE13, LE14" disabled={!gang} />
          {!gang && <span className="small muted">Give the team a gang number first.</span>}
          {gang && prefixes && <span className="small muted">Reads as: {tidy.join(', ') || 'nothing'}</span>}
        </label>
        {!isNew && (
          <div className="team-editor-more">
            <p className="small muted">Members: {team.members || 'nobody'} (change on Match-Ups). Open survey jobs: {team.openSurveys}.</p>
            <div className="actions">
              {team.active
                ? <button type="button" className="btn" disabled={busy} onClick={() => setActive(false)}>Close team</button>
                : <button type="button" className="btn" disabled={busy} onClick={() => setActive(true)}>Reopen team</button>}
              {team.deletable && !confirmDelete && (
                <button type="button" className="btn danger-btn with-icon" disabled={busy} onClick={() => setConfirmDelete(true)}><Trash2 size={16} aria-hidden="true" /> Delete team</button>
              )}
              {team.deletable && confirmDelete && (
                <button type="button" className="btn danger-btn" disabled={busy} onClick={remove}>Yes, delete {team.name}</button>
              )}
            </div>
            {!team.deletable && <p className="small muted">Can’t be deleted, only closed: it has history ({team.usedBy.join(', ')}).</p>}
            {team.active && team.members && <p className="small muted">To close it, move its members out on Match-Ups first.</p>}
          </div>
        )}
        {error && <p className="notice-line notice-bad">{error}</p>}
      </div>
    </Modal>
  );
}
